import React, { useState, useEffect, useRef, useCallback } from "react";
import { Constants, CommonConstants } from "../../../constants/appConstants";
import { SummaryRequest } from "../../../models/csos/summaryRequest";
import { SummaryResponse } from "../../../models/csos/summaryResponse";
import { Order } from "../../../models/csos/order";
import { OrderColumnWidthsSignedTab } from "../../../models/csos/orderColumnWidthsSignedTab";
import { csosApiService } from "../../../services/api/csosApiService";
import { dateService } from "../../../services/dateService";
import { useOrderDetailsService } from "../../../services/component/orderDetailsService";
import LoadingContainer from "../../loadingContainer/LoadingContainer";
import "./SignedTable.css";

interface ColDef {
  id: string;
  title: string;
  value: string;
}

const signedColumns: ColDef[] = [
  { id: "orderId", title: "Order Number", value: "ORDER #" },
  { id: "deaLicenseId", title: "DEA Registration Number", value: "DEA #" },
  { id: "uniqueTrackingNum", title: "Unique Tracking Number", value: "UTN" },
  { id: "purchaseOrderId", title: "Purchase Order Number", value: "PO #" },
  { id: "customerName", title: "Account Name", value: "ACCOUNT NAME" },
  { id: "account", title: "Customer Number", value: "DC-ACCOUNT" },
  { id: "lastUpdatedTime", title: "Time Order was updated", value: "LAST UPDATED TIME" },
  { id: "lastUpdatedUser", title: "Order Signed by User", value: "SIGNED BY" },
];

const SignedTable: React.FC = () => {
  const orderDetailsService = useOrderDetailsService();
  const [response, setResponse] = useState<SummaryResponse>(new SummaryResponse("0", "0", 0, []));
  const [loading, setLoading] = useState(false);
  const [pageText, setPageText] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [backward, setBackward] = useState(false);
  const [forward, setForward] = useState(true);
  const [refreshEnabled, setRefreshEnabled] = useState(true);
  const [displayRefresh, setDisplayRefresh] = useState("active-refresh");
  const [colWidths, setColWidths] = useState<OrderColumnWidthsSignedTab>(new OrderColumnWidthsSignedTab());
  const [sortClasses, setSortClasses] = useState<Record<string, string>>(
    Object.fromEntries(signedColumns.map((c) => [c.id, ""]))
  );
  const [sortfield, setSortfield] = useState("");
  const [sortType, setSortType] = useState("");
  const pageInputRef = useRef<HTMLInputElement>(null);
  const limit = Constants.LIMIT;
  const pageStart = Constants.START_PAGE;
  const onInitFlagRef = useRef(true);
  const refreshIntervalsRef = useRef<number[]>([]);

  const getOrderData = useCallback(
    (sType: string, status: string, sField: string, lim: number, offset: number) => {
      const summaryRequest = new SummaryRequest();
      summaryRequest.sort = sType + sField;
      summaryRequest.status = status;
      summaryRequest.limit = lim;
      summaryRequest.offset = offset;
      csosApiService.fetchtOrdersData(summaryRequest).then((res) => {
        setLoading(false);
        if (res.body) {
          const r = res.body as SummaryResponse;
          const normalizedOrders = r.orders ?? [];
          setResponse({ ...r, orders: normalizedOrders });
          const totalRecords = Number(r.totalNumberOfRecords ?? 0);
          let tp: number;
          if (totalRecords % lim === 0) {
            tp = totalRecords / lim;
          } else {
            tp = Math.floor(totalRecords / lim) + 1;
          }
          setTotalPage(tp);
          if (onInitFlagRef.current) {
            onInitFlagRef.current = false;
            if (tp === pageStart) setForward(false);
          }
          calcColWidths(normalizedOrders);
        }
      }).catch(() => {
        setLoading(false);
      });
    },
    []
  );

  useEffect(() => {
    setRefreshEnabled(false);
    onInitFlagRef.current = true;
    setBackward(false);
    setForward(true);
    setPageText(pageStart);
    setDisplayRefresh("active-refresh");
    setLoading(false);
    getOrderData("", CommonConstants.SIGNED, "", limit, pageStart);
    return () => {
      clearAllSignedIntervals();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const unsub = orderDetailsService.signedTabrefresh$.subscribe((fromPage) => {
      if (fromPage) {
        setRefreshEnabled(false);
        doRefresh(fromPage);
      }
    });
    return () => unsub();
    // eslint-disable-next-line
  }, [orderDetailsService]);

  const calcColWidths = (orders: Order[] = []) => {
    const widths = new OrderColumnWidthsSignedTab();
    if (orders) {
      for (const order of orders) {
        for (const key in widths) {
          const val = (order as any)[key];
          if (val && val.length > widths[key] && val.length > key.length) {
            widths[key] = val.length;
          }
        }
      }
    }
    setColWidths(widths);
  };

  const validateButton = (page: number, tp: number) => {
    setBackward(+page !== 1);
    setForward(+page !== +tp);
  };

  const nextPage = () => {
    const newPage = pageText + 1;
    setPageText(newPage);
    validateButton(newPage, totalPage);
    const offset = limit * (newPage - 1) + 1;
    getOrderData(sortType, CommonConstants.SIGNED, sortfield, limit, offset);
  };

  const prevPage = () => {
    const newPage = pageText - 1;
    setPageText(newPage);
    validateButton(newPage, totalPage);
    const offset = limit * (newPage - 1) + 1;
    getOrderData(sortType, CommonConstants.SIGNED, sortfield, limit, offset);
  };

  const assignPage = (value: string) => {
    const page = parseInt(value, 10);
    if (page <= totalPage && page > 0 && !isNaN(page)) {
      setPageText(page);
      const offset = limit * (page - 1) + 1;
      getOrderData(sortType, CommonConstants.SIGNED, sortfield, limit, offset);
    }
    validateButton(page, totalPage);
  };

  const sortTable = (colId: string) => {
    const currentClass = sortClasses[colId] || "";
    const updated: Record<string, string> = {};
    signedColumns.forEach((c) => { updated[c.id] = ""; });
    let newSortType = "";
    let newSortField = "";
    if (currentClass === "") {
      updated[colId] = "glyphicon glyphicon-chevron-up";
      newSortType = "%2B";
      newSortField = colId;
    } else if (currentClass === "glyphicon glyphicon-chevron-up") {
      updated[colId] = "glyphicon glyphicon-chevron-down";
      newSortType = "-";
      newSortField = colId;
    }
    setSortClasses(updated);
    setSortType(newSortType);
    setSortfield(newSortField);
    const offset = limit * (pageText - 1) + 1;
    getOrderData(newSortType, CommonConstants.SIGNED, newSortField, limit, offset);
  };

  const clearAllSignedIntervals = () => {
    refreshIntervalsRef.current.forEach((id) => clearInterval(id));
    refreshIntervalsRef.current = [];
  };

  const doRefresh = (fromPage: string) => {
    if (!refreshEnabled) {
      setLoading(true);
      setSortfield("");
      setSortType("");
      getOrderData("", CommonConstants.SIGNED, "", limit, pageStart);
      if (fromPage === Constants.FROM_SIGNED_PAGE) {
        setRefreshEnabled(true);
        setDisplayRefresh("inactive-refresh");
        const timer = window.setInterval(() => {
          setRefreshEnabled(false);
          setDisplayRefresh("active-refresh");
          clearAllSignedIntervals();
        }, 30000);
        refreshIntervalsRef.current.push(timer);
      }
      validateButton(pageStart, totalPage);
      if (pageInputRef.current) pageInputRef.current.value = "1";
    }
  };

  const checkPagination = (): boolean => {
    return response != null && Number(response.totalNumberOfRecords ?? 0) > 0;
  };

  const handleOrderClick = (order: Order) => {
    orderDetailsService.announceOrder(order);
  };

  const getColStyle = (minWidth: number) => {
    if (minWidth !== 0) return { minWidth: `${30 + minWidth * 7}px` };
    return { minWidth: `${window.screen.width / 12.1}px` };
  };

  return (
    <div>
      <div className="signed-section"></div>
      <LoadingContainer loading={loading} />
      <div className="signed-pagination-section">
        <div onClick={() => doRefresh(Constants.FROM_SIGNED_PAGE)}>
          <span className={`refresh ${displayRefresh}`}>
            {!refreshEnabled && <img src="/assets/images/refreshb.png" alt="refresh" />}
            {refreshEnabled && <img className="valid-image" src="/assets/images/refresh.gif" alt="refresh" />}
            {Constants.REFRESH}
          </span>
        </div>
        {checkPagination() && (
          <span>
            <span>{Constants.PAGE}</span>
            <input
              ref={pageInputRef}
              type="text"
              defaultValue="1"
              className="signed-pagination-text"
              onKeyUp={(e) => { if (e.key === "Enter") assignPage((e.target as HTMLInputElement).value); }}
              onChange={(e) => assignPage(e.target.value)}
            />
            <span> {Constants.OF} {totalPage}</span>
            {backward && (
              <span onClick={prevPage}>
                <img className="valid-image" src="/assets/images/icon-arrow-left.png" alt="prev" />
              </span>
            )}
            {forward && (
              <span onClick={nextPage}>
                <img className="valid-image" src="/assets/images/icon-arrow-right.png" alt="next" />
              </span>
            )}
          </span>
        )}
      </div>

      <div className="signed-table-container">
        <table className="table table-bordered">
          <thead>
            <tr className="tableHeader">
              {signedColumns.map((col) => (
                <th
                  key={col.id}
                  style={getColStyle((colWidths as any)[col.id] || 0)}
                  onClick={() => sortTable(col.id)}
                >
                  <span className="text-nowrap" title={col.title}>
                    {col.value}
                    {col.value === "LAST UPDATED TIME" && (
                      <><br />({dateService.getTimeZoneAbbr()})</>
                    )}
                  </span>
                  <span className={sortClasses[col.id] || ""}></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(response.orders ?? []).map((order, idx) => (
              <tr key={idx}>
                <td className="signed-row-td text-nowrap text-center" style={getColStyle(colWidths.orderId)}>
                  <a onClick={() => handleOrderClick(order)}>{order.orderId}</a>
                </td>
                <td className="signed-row-td text-nowrap text-center" style={getColStyle(colWidths.deaLicenseId)}>{order.deaLicenseId}</td>
                <td className="signed-row-td text-nowrap text-center" style={getColStyle(colWidths.uniqueTrackingNum)}>
                  <a onClick={() => handleOrderClick(order)}>{order.uniqueTrackingNum}</a>
                </td>
                <td className="signed-row-td text-nowrap text-left" style={getColStyle(colWidths.purchaseOrderId)}>{order.purchaseOrderId}</td>
                <td className="signed-row-td text-nowrap text-center" style={getColStyle(colWidths.customerName)}>{order.customerName}</td>
                <td className="signed-row-td text-nowrap text-center" style={getColStyle(colWidths.account)}>{order.account}</td>
                <td className="signed-row-td text-nowrap text-center" style={getColStyle(colWidths.lastUpdatedTime)}>
                  {dateService.localizeDate(order.lastUpdatedTime, "MM/DD/YYYY hh:mm A", "MM/DD/YYYY hh:mm A")}
                </td>
                <td className="signed-row-td text-nowrap text-center" style={getColStyle(colWidths.lastUpdatedUser)}>{order.lastUpdatedUser}</td>
              </tr>
            ))}
            {Number(response.totalNumberOfRecords ?? 0) === 0 && !onInitFlagRef.current && (
              <tr><td colSpan={12}>No Signed Orders Found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SignedTable;
